"use client";

import { Delete } from "lucide-react";
import { KEYBOARD_ROWS } from "../_logic/constants";
import type { LetterStatus, ScoredGuess } from "../_logic/types";
import { STATUS_CLASSES } from "./tileStyles";

interface WordleKeyboardProps {
  guesses: ScoredGuess[];
  onKey: (key: string) => void;
  disabled: boolean;
}

const RANK: Record<LetterStatus, number> = { absent: 0, present: 1, correct: 2 };

function bestStatuses(guesses: ScoredGuess[]): Map<string, LetterStatus> {
  const best = new Map<string, LetterStatus>();
  guesses.forEach(({ word, statuses }) =>
    word.split("").forEach((letter, i) => {
      const prev = best.get(letter);
      if (!prev || RANK[statuses[i]] > RANK[prev]) best.set(letter, statuses[i]);
    })
  );
  return best;
}

/**
 * On-screen keyboard that colors each key by the best result that letter has had so far.
 * @param props Guesses so far, the key handler, and whether input is locked.
 * @returns The keyboard.
 */
export function WordleKeyboard({ guesses, onKey, disabled }: WordleKeyboardProps) {
  // Derived values
  const statuses = bestStatuses(guesses);

  // Render
  return (
    <div className="flex w-full flex-col gap-1.5" aria-label="Keyboard">
      {KEYBOARD_ROWS.map((row, i) => (
        <div key={i} className="flex justify-center gap-1.5">
          {i === 1 && <div className="flex-[0.5]" />}
          {row.map((key) => {
            const isWide = key === "ENTER" || key === "BACKSPACE";
            const status = statuses.get(key);
            const look = status ? STATUS_CLASSES[status] : "bg-slate-100 text-slate-900 hover:bg-slate-200";
            return (
              <button
                key={key}
                type="button"
                onClick={() => onKey(key)}
                disabled={disabled}
                aria-label={key === "BACKSPACE" ? "Delete" : key === "ENTER" ? "Enter" : key}
                className={`flex h-14 items-center justify-center rounded-md text-sm font-semibold transition-colors active:scale-95 disabled:cursor-not-allowed ${
                  isWide ? "flex-[1.5] text-xs" : "flex-1"
                } ${look}`}
              >
                {key === "BACKSPACE" ? <Delete className="h-5 w-5" /> : key}
              </button>
            );
          })}
          {i === 1 && <div className="flex-[0.5]" />}
        </div>
      ))}
    </div>
  );
}
